import * as XLSX from 'xlsx'

export const VENDOR_ID_MAX_LENGTH = 64
export const VENDOR_NAME_MAX_LENGTH = 255
export const VENDOR_IMPORT_MAX_ROWS = 5000

export const VENDOR_ID_PATTERN = /^[A-Za-z0-9][A-Za-z0-9._-]{0,63}$/

export const VENDOR_MESSAGES = {
  missingColumns: 'Excel must include Vendor Name and Vendor ID columns',
  noRows: 'The file has no vendor rows',
  tooManyRows: `The file has more than ${VENDOR_IMPORT_MAX_ROWS} vendor rows`,
  noSheets: 'The workbook has no sheets',
  idRequired: 'Vendor ID is required',
  nameRequired: 'Vendor Name is required',
  idTooLong: `Vendor ID must be ${VENDOR_ID_MAX_LENGTH} characters or fewer`,
  nameTooLong: `Vendor Name must be ${VENDOR_NAME_MAX_LENGTH} characters or fewer`,
  idCharset:
    'Vendor ID may only contain letters, numbers, dots, hyphens, and underscores, and must start with a letter or number',
  nameUnsafe: 'Vendor Name cannot contain HTML or code',
  duplicateId: 'Duplicate Vendor ID in this file',
}

export function stringifyVendorCell(value) {
  if (value == null) return ''
  if (typeof value === 'number' && Number.isFinite(value)) return String(value)
  return String(value).replace(/\u00a0/g, ' ').trim()
}

export function normalizeVendorHeader(value) {
  return stringifyVendorCell(value)
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
}

function vendorNameError(value) {
  const name = stringifyVendorCell(value)
  if (!name) return VENDOR_MESSAGES.nameRequired
  if ([...name].length > VENDOR_NAME_MAX_LENGTH) return VENDOR_MESSAGES.nameTooLong
  if (/<\/?[a-zA-Z!?/][^>]{0,500}>/.test(name)) return VENDOR_MESSAGES.nameUnsafe
  if (/\bon[a-z]+\s*=/i.test(name)) return VENDOR_MESSAGES.nameUnsafe
  if (/(?:javascript|vbscript)\s*:/i.test(name)) return VENDOR_MESSAGES.nameUnsafe
  if (/data\s*:\s*text\s*\/\s*html/i.test(name)) return VENDOR_MESSAGES.nameUnsafe
  if (/;\s*(select|insert|update|delete|drop|union|alter|exec|execute|truncate)\b/i.test(name)) {
    return VENDOR_MESSAGES.nameUnsafe
  }
  if (/'+\s*(or|and)\s+['\d]/i.test(name)) return VENDOR_MESSAGES.nameUnsafe
  if (/\bunion\s+(all\s+)?select\b/i.test(name)) return VENDOR_MESSAGES.nameUnsafe
  if (/\/\*|\*\//.test(name)) return VENDOR_MESSAGES.nameUnsafe
  if (/;\s*--/.test(name)) return VENDOR_MESSAGES.nameUnsafe
  return null
}

export function validateVendorRecord(vendorIdRaw, vendorNameRaw) {
  const errors = []
  let vendorId = ''
  let vendorName = ''

  const idText = stringifyVendorCell(vendorIdRaw)
  if (!idText) {
    errors.push(VENDOR_MESSAGES.idRequired)
  } else if ([...idText].length > VENDOR_ID_MAX_LENGTH) {
    errors.push(VENDOR_MESSAGES.idTooLong)
  } else if (!VENDOR_ID_PATTERN.test(idText)) {
    errors.push(VENDOR_MESSAGES.idCharset)
  } else {
    vendorId = idText
  }

  const nameError = vendorNameError(vendorNameRaw)
  if (nameError) errors.push(nameError)
  else vendorName = stringifyVendorCell(vendorNameRaw)

  if (errors.length) return { ok: false, errors }
  return { ok: true, vendor: { vendor_id: vendorId, vendor_name: vendorName } }
}

function isBlankRow(row) {
  return !row.some((cell) => stringifyVendorCell(cell) !== '')
}

export function parseVendorMatrix(matrix, firstRowNumber = 1) {
  const empty = { fatal: null, errors: [], validRows: [] }
  if (!Array.isArray(matrix) || matrix.length === 0) {
    return { ...empty, fatal: VENDOR_MESSAGES.noRows }
  }

  const headerIndex = matrix.findIndex((row) => Array.isArray(row) && !isBlankRow(row))
  if (headerIndex < 0) return { ...empty, fatal: VENDOR_MESSAGES.noRows }

  const headerRow = matrix[headerIndex] || []
  let nameIndex = -1
  let idIndex = -1
  headerRow.forEach((cell, index) => {
    const key = normalizeVendorHeader(cell)
    if (key === 'vendorname') nameIndex = index
    if (key === 'vendorid') idIndex = index
  })
  if (nameIndex < 0 || idIndex < 0) {
    return { ...empty, fatal: VENDOR_MESSAGES.missingColumns }
  }

  const prepared = []
  for (let index = headerIndex + 1; index < matrix.length; index += 1) {
    const row = Array.isArray(matrix[index]) ? matrix[index] : []
    if (isBlankRow(row)) continue
    const rawId = row[idIndex]
    const rawName = row[nameIndex]
    const record = validateVendorRecord(rawId, rawName)
    prepared.push({
      row: firstRowNumber + index,
      vendor_id: record.ok ? record.vendor.vendor_id : stringifyVendorCell(rawId),
      vendor_name: record.ok ? record.vendor.vendor_name : stringifyVendorCell(rawName),
      messages: record.ok ? [] : [...record.errors],
    })
  }

  if (prepared.length === 0) return { ...empty, fatal: VENDOR_MESSAGES.noRows }
  if (prepared.length > VENDOR_IMPORT_MAX_ROWS) {
    return { ...empty, fatal: VENDOR_MESSAGES.tooManyRows }
  }

  const groups = new Map()
  prepared.forEach((item) => {
    if (!item.vendor_id || item.messages.includes(VENDOR_MESSAGES.idRequired)) return
    if (!VENDOR_ID_PATTERN.test(item.vendor_id)) return
    const key = item.vendor_id.toLowerCase()
    const list = groups.get(key) || []
    list.push(item)
    groups.set(key, list)
  })
  groups.forEach((list) => {
    if (list.length > 1) {
      list.forEach((item) => item.messages.push(VENDOR_MESSAGES.duplicateId))
    }
  })

  const errors = prepared
    .filter((item) => item.messages.length > 0)
    .map((item) => ({
      row: item.row,
      vendor_id: item.vendor_id,
      vendor_name: item.vendor_name,
      message: item.messages.join('; '),
    }))

  const validRows = prepared
    .filter((item) => item.messages.length === 0)
    .map((item) => ({
      row: item.row,
      vendor_id: item.vendor_id,
      vendor_name: item.vendor_name,
    }))

  return { fatal: null, errors, validRows }
}

export function parseVendorWorkbook(workbook) {
  const sheetNames = workbook?.SheetNames || []
  if (!sheetNames.length) {
    return { fatal: VENDOR_MESSAGES.noSheets, errors: [], validRows: [] }
  }
  const sheetName =
    sheetNames.find((name) => String(name).trim().toLowerCase() === 'vendors') || sheetNames[0]
  const sheet = workbook.Sheets?.[sheetName]
  if (!sheet || !sheet['!ref']) {
    return { fatal: VENDOR_MESSAGES.noRows, errors: [], validRows: [] }
  }
  const range = XLSX.utils.decode_range(sheet['!ref'])
  const matrix = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    raw: false,
    blankrows: true,
  })
  return parseVendorMatrix(matrix, range.s.r + 1)
}

export function parseVendorFileBuffer(arrayBuffer) {
  const workbook = XLSX.read(arrayBuffer, { type: 'array', cellDates: false })
  return parseVendorWorkbook(workbook)
}
